import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { rankingsApi } from "../api";
import { RankingUser } from "../types";

const generateMonthlyPoints = (
  eventAcquiredPoints: Array<{
    event_name: string;
    point: number;
    date: string;
  }>,
) => {
  const monthlyMap = new Map<string, number>();
  eventAcquiredPoints.forEach((item) => {
    const date = new Date(item.date);
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    monthlyMap.set(month, (monthlyMap.get(month) || 0) + item.point);
  });
  return Array.from(monthlyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, points]) => ({ month, points }));
};

interface RankingsContextValue {
  rankings: RankingUser[];
  isLoading: boolean;
  error: string | null;
  fetchAll: () => Promise<void>;
  lastFetchedAt: number | null;
}

const RankingsContext = createContext<RankingsContextValue | null>(null);

export const RankingsProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [rankings, setRankings] = useState<RankingUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await rankingsApi.getYearly();
      setRankings(
        response.rankings
          .map((r) => ({
            rank: r.rank,
            name: r.name,
            points: r.point,
            isMe: r.is_login_member,
            monthlyPoints: generateMonthlyPoints(r.event_acquired_points),
            eventAcquiredPoints: r.event_acquired_points,
          }))
          .sort((a, b) => a.rank - b.rank),
      );
      setLastFetchedAt(Date.now());
    } catch {
      setError("ランキングの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAll();
    } else {
      setRankings([]);
    }
  }, [isAuthenticated, fetchAll]);

  return (
    <RankingsContext.Provider
      value={{ rankings, isLoading, error, fetchAll, lastFetchedAt }}
    >
      {children}
    </RankingsContext.Provider>
  );
};

export const useRankingsContext = (): RankingsContextValue => {
  const ctx = useContext(RankingsContext);
  if (!ctx)
    throw new Error("useRankingsContext must be used within RankingsProvider");
  return ctx;
};
